import React, { useState, useEffect } from "react";
import { ICellRendererParams } from "ag-grid-community";
import Checkbox from "src/components/Checkbox/Checkbox";

interface CompleteRendererProps extends ICellRendererParams {
  data: {
    scoreId: string;
    complete: boolean;
  };
  onCompleteChange?: (scoreId: string, complete: boolean) => Promise<void> | void;
}

const pendingCompleteByScoreId = new Map<string, { value: boolean; saving: boolean }>();

const CompleteRenderer: React.FC<CompleteRendererProps> = ({ data, onCompleteChange }) => {
  const [, forceRender] = useState(0);
  const scoreId = data?.scoreId;
  const pendingState = scoreId ? pendingCompleteByScoreId.get(scoreId) : undefined;
  const complete = pendingState ? pendingState.value : !!data?.complete;
  const isSaving = !!pendingState?.saving;

  // Sync with data prop ONLY when the actual boolean value changes (e.g. from a
  // websocket-triggered refetch). Do NOT list `data` (the object reference) as a
  // dependency — AG Grid rebuilds row objects on every parent re-render, so a new
  // `data` reference with the same stale `complete: false` would immediately undo
  // any optimistic update made before the server response arrives.
  useEffect(() => {
    if (!scoreId || !data) {
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.complete]);

  if (!data) return null;

  return (
    <div 
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox
        id={`complete-checkbox-${data.scoreId}`}
        label=""
        value={complete}
        disabled={isSaving}
        onChange={async (e) => {
          e.stopPropagation();
          if (!scoreId) {
            return;
          }

          const newValue = e.target.checked;
          pendingCompleteByScoreId.set(scoreId, { value: newValue, saving: true });
          forceRender((v) => v + 1);

          if (onCompleteChange) {
            try {
              await onCompleteChange(data.scoreId, newValue);
              pendingCompleteByScoreId.set(scoreId, { value: newValue, saving: false });
            } catch {
              pendingCompleteByScoreId.delete(scoreId);
            }
            forceRender((v) => v + 1);
            return;
          }
          pendingCompleteByScoreId.set(scoreId, { value: newValue, saving: false });
          forceRender((v) => v + 1);
        }}
      />
    </div>
  );
};

export { CompleteRenderer };

